import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router-dom';

import { stripesConnect } from '@folio/stripes/core';

import {
  TEAMS_DEFAULT_SORT_FIELD,
  RESULT_COUNT_INCREMENT,
  getTeamListUrl,
} from '../../../constants';
import { getParams } from '../../../utils';
import { useLocationReset } from '../../../hooks';
import { TeamList } from '../../components/team/team-list';

const resetData = () => {};

const TeamsListRoute = ({
  mutator,
  location,
  history,
  children,
}) => {
  const [teams, setTeams] = useState([]);
  const [teamsCount, setTeamsCount] = useState(0);
  const [teamsOffset, setTeamsOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const loadTeams = (offset) => {
    setIsLoading(true);

    const loadJobsPromise = mutator.teams.GET({
      params: {
        limit: RESULT_COUNT_INCREMENT,
        offset,
        ...(location.search && getParams(location.search, TEAMS_DEFAULT_SORT_FIELD)),
      }
    })
      .then(teamsResponse => {
        if (!offset) {
          setTeamsCount(teamsResponse.totalRecords);
        }

        setTeams((prev) => [...prev, ...teamsResponse.teams]);
      });

    return loadJobsPromise
      .finally(() => setIsLoading(false));
  };

  const onNeedMoreData = useCallback(
    () => {
      const newOffset = teamsOffset + RESULT_COUNT_INCREMENT;

      loadTeams(newOffset)
        .then(() => {
          setTeamsOffset(newOffset);
        });
    },
    [teamsOffset],
  );

  const refreshList = () => {
    setTeams([]);
    setTeamsCount(0);
    setTeamsOffset(0);
    loadTeams(0);
  };

  useEffect(
    () => {
      refreshList();
    },
    [location.search, location.state],
  );

  useLocationReset(history, location, getTeamListUrl(), refreshList);

  return (
    <TeamList
      isLoading={isLoading}
      teams={teams}
      teamsCount={teamsCount}
      resetData={resetData}
      onNeedMoreData={onNeedMoreData}
    >
      {children}
    </TeamList>
  );
};

TeamsListRoute.manifest = Object.freeze({
  teams: {
    type: 'okapi',
    path: 'tasks/teams',
    fetch: false,
    accumulate: true,
    throwErrors: false,
  },
  resultCount: { initialValue: RESULT_COUNT_INCREMENT },
});

TeamsListRoute.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  location: ReactRouterPropTypes.location.isRequired,
  mutator: PropTypes.object.isRequired,
  children: PropTypes.node,
};

export default withRouter(stripesConnect(TeamsListRoute));
